import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const initialToken = localStorage.getItem("OrviSlideToken");
const initialAuth = localStorage.getItem("OrviSlideAuth");

const initialState = {
  auth: initialAuth ? JSON.parse(initialAuth) : null,
  token: initialToken,
  isAuthenticated: !!initialToken,
  loading: false,
  error: null,
  loginStatus: "idle",
  refreshTokenLoading: false,
  refreshTokenError: null,
  forgotPasswordLoading: false,
  forgotPasswordError: null,
  resetPasswordLoading: false,
  resetPasswordError: null,
};

// Async thunk for login
export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(credentials),
        },
      );
      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || "Login failed");
      }
      if (data.data.access_token) {
        localStorage.setItem("OrviSlideToken", data.data.access_token);
        localStorage.setItem("OrviSlideAuth", JSON.stringify(data.data));
      }
      return data;
    } catch (error) {
      return rejectWithValue(error.message || "Network error");
    }
  },
);

export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("OrviSlideToken");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/logout`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const data = await response.json();
      localStorage.removeItem("OrviSlideToken");
      localStorage.removeItem("OrviSlideAuth");

      if (!response.ok) {
        return rejectWithValue(data.message || "Logout failed");
      }
      return data;
    } catch (error) {
      return rejectWithValue(error.message || "Network error");
    }
  },
);

export const refreshTokenUser = createAsyncThunk(
  "auth/refreshTokenUser",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("OrviSlideToken");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/refresh`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || "Refresh token failed");
      }

      if (data.access_token) {
        localStorage.setItem("OrviSlideToken", data.access_token);
        if (data.data) {
          localStorage.setItem("OrviSlideAuth", JSON.stringify(data.data));
        }
      }
      return data;
    } catch (error) {
      return rejectWithValue(error.message || "Network error");
    }
  },
);

export const forgotPasswordUser = createAsyncThunk(
  "auth/forgotPasswordUser",
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/forgot-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ email }),
        },
      );
      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || "Forgot password failed");
      }
      return data;
    } catch (error) {
      return rejectWithValue(error.message || "Network error");
    }
  },
);

export const resetPasswordUser = createAsyncThunk(
  "auth/resetPasswordUser",
  async ({ token, data }, { rejectWithValue }) => {
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/reset-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(data),
        }
      );
      const resetData = await response.json();
      if (!response.ok) {
        return rejectWithValue(resetData.message || "Reset password failed");
      }
      return resetData;
    } catch (error) {
      return rejectWithValue(error.message || "Network error");
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.loginStatus = "loading";
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        // state.auth = action.payload.data;
        state.loginStatus = "succeeded";
        state.isAuthenticated = true;
        // state.token = action.payload.data.access_token;
        if (action.payload?.data) {
          state.auth = action.payload.data;
          state.token = action.payload.data.access_token;
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.loginStatus = "failed";
        state.error = action.payload;
      })
      // logout reducers
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.loading = false;
        state.auth = null;
        state.token = null;
        state.loginStatus = "idle";
        state.isAuthenticated = false;
        state.error = null;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ✅ Refresh token reducers
      .addCase(refreshTokenUser.pending, (state) => {
        state.refreshTokenLoading = true;
      })
      .addCase(refreshTokenUser.fulfilled, (state, action) => {
        state.refreshTokenLoading = false;
        // state.auth = action.payload.data;
        state.isAuthenticated = true;
        state.loginStatus = "succeeded";
        // state.token = action.payload.data.access_token;
        // Only update if payload and token exist
        if (action.payload?.data) {
          state.auth = action.payload.data;
          state.token = action.payload.data.access_token;
        }
      })
      .addCase(refreshTokenUser.rejected, (state, action) => {
        state.refreshTokenLoading = false;
        state.loginStatus = "failed";
        state.isAuthenticated = false;
        state.token = null;
        state.refreshTokenError = action.payload;
      })
      // ✅ Forgot password reducers

      .addCase(forgotPasswordUser.pending, (state) => {
        state.forgotPasswordLoading = true;
        state.forgotPasswordError = null;
      })
      .addCase(forgotPasswordUser.fulfilled, (state) => {
        state.forgotPasswordLoading = false;
      })
      .addCase(forgotPasswordUser.rejected, (state, action) => {
        state.forgotPasswordLoading = false;
        state.forgotPasswordError = action.payload;
      })

      // ✅ Reset password reducers
      .addCase(resetPasswordUser.pending, (state) => {
        state.resetPasswordLoading = true;
        state.resetPasswordError = null;
      })
      .addCase(resetPasswordUser.fulfilled, (state) => {
        state.resetPasswordLoading = false;
      })
      .addCase(resetPasswordUser.rejected, (state, action) => {
        state.resetPasswordLoading = false;
        state.resetPasswordError = action.payload;
      })
  },
});

export default authSlice.reducer;

export const selectUserPermissions = (state) => {
  const roles = state.auth?.auth?.admin?.roles || [];
  const permissions = roles.flatMap((role) => role.permissions || []);
  return permissions.map((p) => p.name);
};

export const selectUserRole = (state) => {
  const roles = state.auth?.auth?.admin?.roles || [];
  return roles.length > 0 ? roles[0].name : null;
};

export const selectHasPermission = (state, permissionName) => {
  const role = selectUserRole(state);
  const isAdminRole =
    role && ["admin", "super admin", "super"].includes(role.toLowerCase());
  if (isAdminRole) return true;

  const permissions = selectUserPermissions(state);
  return permissions.includes(permissionName);
};
